import "../styles.scss";
import { Box, Button, makeStyles } from "@material-ui/core";
import { Edit2 as EditIcon } from "react-feather";
import { FranchiseAgreementAcceptance } from "@hubcapp/graphql/types/gateway";
import { getContactPreferencesString, getSafeDate } from "@hubcapp/utils";
import ModalModifyPersonalDetailsComponent from "./ModalModifyPersonalDetailsComponent";
import React, { useState } from "react";
import clsx from "clsx";

const useStyles = makeStyles(theme => ({
  subText: {
    fontWeight: 400,
    "&.bsm": {
      fontWeight: 300,
    },
    fontSize: "18px",
    lineHeight: "27px",
    color: "#2B2D32",
  },
  title: {
    fontWeight: 700,
    "&.bsm": {
      fontWeight: 500,
    },
    fontSize: "18px",
    lineHeight: "27px",
    color: "#2B2929",
    padding: "6px 50px 6px 0px",
    [theme.breakpoints.down("sm")]: {
      padding: "6px 6px 6px 0px",
    },
  },
  result: {
    fontWeight: 400,
    "&.bsm": {
      fontWeight: 300,
    },
    fontSize: "18px",
    lineHeight: "27px",
    color: "#2B2929",
  },
  sectionStyle: {
    marginBottom: "80px",
    "@media (max-width: 767px)": {
      marginBottom: "56px",
    },
  },
  editButton: {
    color: "#07828D",
    fontSize: "18px",
    fontWeight: 700,
    textTransform: "none",
  },
  editButtonBsm: {
    color: "#3933B7",
    fontSize: "16px",
    fontWeight: 400,
  },
}));

type Props = {
  data: FranchiseAgreementAcceptance | undefined;
  brand: string;
  refetch: () => void;
};

const PersonalDetailsComponent: React.FC<Props> = ({
  data,
  refetch,
  brand,
}) => {
  const [isOpenedModal, setIsOpenedModal] = useState(false);
  const classes = useStyles();
  return (
    <div className={classes.sectionStyle}>
      <p
        className={clsx(classes.subText, {
          bsm: brand === "BSM",
        })}
      >
        Please check all your details to make sure they&apos;re accurate. Use
        the Edit button below to make any necessary changes.
      </p>
      <table>
        <tr>
          <td
            className={clsx(classes.title, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            Title
          </td>
          <td
            className={clsx(classes.result, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            {data?.title || "-"}
          </td>
        </tr>
        <tr>
          <td
            className={clsx(classes.title, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            First name
          </td>
          <td
            className={clsx(classes.result, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            {data?.firstName || "-"}
          </td>
        </tr>
        <tr>
          <td
            className={clsx(classes.title, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            Surname
          </td>
          <td
            className={clsx(classes.result, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            {data?.surname || "-"}
          </td>
        </tr>
        <tr>
          <td
            className={clsx(classes.title, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            Date of birth
          </td>
          <td
            className={clsx(classes.result, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            {getSafeDate(data?.dateOfBirth) || "-"}
          </td>
        </tr>
        <tr>
          <td
            className={clsx(classes.title, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            Address
          </td>
          <td
            className={clsx(classes.result, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            {data?.address || "-"}
          </td>
        </tr>
        <tr>
          <td
            className={clsx(classes.title, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            City
          </td>
          <td
            className={clsx(classes.result, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            {data?.city || "-"}
          </td>
        </tr>
        <tr>
          <td
            className={clsx(classes.title, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            Postcode
          </td>
          <td
            className={clsx(classes.result, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            {data?.zipCode || "-"}
          </td>
        </tr>
        <tr>
          <td
            className={clsx(classes.title, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            Email address
          </td>
          <td
            className={clsx(classes.result, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            {data?.emailAddress || "-"}
          </td>
        </tr>
        <tr>
          <td
            className={clsx(classes.title, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            Phone number
          </td>
          <td
            className={clsx(classes.result, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            {data?.phoneNumber || "-"}
          </td>
        </tr>
        <tr>
          <td
            className={clsx(classes.title, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            Driving licence number
          </td>
          <td
            className={clsx(classes.result, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            {data?.drivingNumber || "-"}
          </td>
        </tr>
        <tr>
          <td
            className={clsx(classes.title, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            Instructor number
          </td>
          <td
            className={clsx(classes.result, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            {data?.adiNumber || "-"}
          </td>
        </tr>
        <tr>
          <td
            className={clsx(classes.title, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            Contact preferences
          </td>
          <td
            className={clsx(classes.result, {
              bsm: brand === "BSM",
              aa: brand !== "BSM",
            })}
          >
            {getContactPreferencesString(data?.communication?.marketing)}
          </td>
        </tr>
      </table>
      <Box paddingTop={3}>
        <Button
          variant="text"
          onClick={() => setIsOpenedModal(true)}
          endIcon={<EditIcon fill="currentColor" />}
          className={clsx(classes.editButton, {
            [classes.editButtonBsm]: brand === "BSM",
          })}
        >
          Edit your details
        </Button>
      </Box>
      <ModalModifyPersonalDetailsComponent
        data={data}
        isOpened={isOpenedModal}
        setIsOpenedModal={setIsOpenedModal}
        refetch={refetch}
      />
    </div>
  );
};

export default PersonalDetailsComponent;
